import { fileToBase64 } from '@lib/files/file-to-base64';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { ApiError } from '@lib/network/api';
import { areAllValuesEmpty } from '@lib/utils/object';
import {
  resolveZeroResultReason,
  type ZeroResultReason,
} from '@pages/Search/utils/zeroResultReason';
import { debugStore } from '@stores/Debug';
import { Library, isLibraryTranscriptOnly } from '@stores/Library/Library';
import { SearchRequest, SearchResultItem, SegmentType } from '@stores/Library/LibrarySearch';
import { BatchNotification, notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media';
import { action, computed, makeObservable, observable } from 'mobx';
import { MomentSelection } from '../MomentSelection/MomentSelection';
import { LibraryManager } from './LibraryManager';

/**
 * The backend's "you can't search this library" answer, and only that answer.
 *
 * Matched on `code` as well as status, not status alone. A bare 403 check would let
 * any future 403 the search endpoint grows — plan gating, quota, read-only
 * enforcement — render "It may have been deleted, or you may no longer have access"
 * and offer a primary CTA that drops the library from the scope and persists it.
 * Anything else falls through to `search-failed`, whose offer is "Try again" rather
 * than a destructive prune.
 */
function isLibraryForbiddenError(error: Error | null): boolean {
  return (
    error instanceof ApiError && error.status === 403 && error.code === 'user.library.forbidden'
  );
}

export class LibraryManagerMultiLibrarySearch extends ReactionManager {
  readonly momentSelection: MomentSelection;
  private _searchTrackTimeout: ReturnType<typeof setTimeout> | null = null;

  @observable.deep
  // Uses qdrant id for clipId.
  request: SearchRequest | null = null;

  @observable
  resultType: SegmentType = 'visual';

  @observable
  searchClipPreview: { clip: SearchResultItem; preview: string } | null = null;

  @observable
  mediaId: string | null = null;

  @computed
  get atLeastOneMomentSelected() {
    return this.momentSelection.selectedMoments.size > 0;
  }

  @computed
  get moreThanOneMomentSelected() {
    return this.momentSelection.selectedMoments.size > 1;
  }

  /**
   * Where a disallowed or unspecified mode lands: transcript when the whole
   * scope is transcript-only (visual is disabled there and returns nothing),
   * else the visual default. Single source for every fallback site — the
   * setter's coercion below and the URL parser in SearchParamsInURL.
   */
  @computed
  get fallbackResultType(): SegmentType {
    return this.allScopeIsTranscriptOnly ? 'transcript' : 'visual';
  }

  /**
   * DO NOT add a reaction that re-coerces `resultType` when the selected libraries change.
   *
   * It looks like a bug and gets reported as one: pick a transcript-only library in the search
   * bar's picker and the mode stays on Visual, so the search returns nothing. It is intended.
   *
   * Why coercing breaks things: the scope is **multi-library**. Any rule strong enough to fix
   * the single-transcript-only-library case also fires while the user is *assembling* a
   * selection — deselect your last visual library on the way to picking a different one and the
   * mode flips to Transcript underneath you, then does not flip back (flipping back would be
   * worse: it would override a Transcript mode the user chose deliberately on a normal library).
   * A mode that changes itself mid-selection is harder to understand than one that stays put.
   *
   * The intended behaviour instead: let the user run the visual search, get no results, and see
   * the transcript-search suggestion — the empty state that already exists for this
   * (`TranscriptOnlyFootnote` / the suggestion in the search empty state), with a button to
   * switch mode. The user stays in control and learns why.
   *
   * Note this is NOT in tension with `findMoments` picking transcript mode
   * (`useLibraryVerbs.ts`). That is a *navigation* choosing an initial mode for a scope it is
   * creating from nothing — no selection is in progress and nothing the user set is overridden.
   * Choosing an initial mode is fine; changing it under them afterwards is not.
   */

  @action
  setResultType = (type: SegmentType) => {
    // Store-level backstop for the product-wide unified-search disable: the UI
    // and URL entry points are individually gated on the same flag, but this
    // setter is the single sink they all flow through (and the reaction below
    // fires the billed /search/unified fetch), so an ungated future caller must
    // not be able to revive the mode.
    if (type === 'unified' && !debugStore.showUnifiedSearch) {
      type = this.fallbackResultType;
    }
    if (type === this.resultType) return;
    this.resultType = type;
    trackingStore.searchResultTypeChanged(type);
  };

  @action
  setMediaId(mediaId: string | null) {
    this.mediaId = mediaId;
  }

  @computed
  get hasSearchTerms() {
    return (
      (this.resultType === 'transcript' && !!this.request?.text) ||
      (this.resultType !== 'transcript' && !!this.request)
    );
  }

  @computed
  get allLibrariesFinishedSearch() {
    return this.libraryManager.selectedLibrariesToSearch.every((l) => !l.search.fetching);
  }

  @computed
  get anyLibrarySearching() {
    return this.libraryManager.selectedLibrariesToSearch.some((l) => l.search.fetching);
  }

  /**
   * Whether every library in scope has answered the request being asked *right
   * now* — not merely "isn't fetching" and not merely "has data".
   *
   * Both weaker tests fail the same way. `allLibrariesFinishedSearch` above only
   * checks `fetching`, which is false throughout the 400ms debounce after every
   * keystroke: nothing in flight, nothing asked either. And `!!search.data` is
   * true from 400ms after boot for the whole session, because the store's request
   * reaction is `fireImmediately: true` and the resulting fetch returns an empty
   * `{results: [], media: {}}` — so a gate built on it is permanently open. Either
   * one lets a zero-result state render "No moments match “d”" over a search that
   * hasn't started, which is the flicker documented on
   * `shouldSuggestTranscriptSearch` (it escapes only because a transcript-only
   * scope skips the fetch outright).
   *
   * `isSettledForCurrentRequest` compares the answer against the question, which is
   * the thing actually being asked here.
   *
   * Vacuously true for an empty scope, deliberately: with no library to ask there is
   * nothing to wait for, so the scope-only reasons still answer instantly.
   */
  @computed
  get allLibrariesSettledSearch() {
    return this.libraryManager.selectedLibrariesToSearch.every(
      (l) => l.search.isSettledForCurrentRequest,
    );
  }

  /**
   * Selected libraries we hold but currently can't read — the store exists, so the
   * backend still lists the library, but `data` is null because its last fetch
   * failed (`keepDataOnFetchError` is false).
   *
   * They fall through every other count: `selectedLibrariesToSearch` filters them
   * out, so they aren't searchable, and `knownLibraryIds` still contains them, so
   * they aren't unreachable either. Left unmodelled, the scope silently shrinks and
   * a zero result gets blamed on the query — "No moments match X" about a query
   * that never ran against half the scope.
   */
  @computed
  get unresolvedLibraryIdsInScope(): string[] {
    return Array.from(this.libraryManager.selectedLibraryIdsToSearch).filter(
      (id) =>
        this.libraryManager.knownLibraryIds.has(id) && !this.libraryManager.libraryById(id)?.data,
    );
  }

  /**
   * Libraries in the current search scope that can't return visual
   * results (podcasts / audio-only). Drives the warning/error badges
   * on the Visual and Unified toggles and the mixed-scope footnote.
   */
  @computed
  get transcriptOnlyLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter((l) =>
      isLibraryTranscriptOnly(l.data),
    );
  }

  /**
   * Whether every one of `libraryIds` is transcript-only — the rule behind
   * `fallbackResultType`, exposed for callers deciding a mode for a *prospective* scope before
   * it has been applied (`useLibraryVerbs.findMoments`). Ids that don't resolve are ignored.
   *
   * Takes ids rather than reading the current scope so the quantifier lives in one place: it
   * was briefly written out twice, and "all" silently becoming "some" in one copy is invisible.
   */
  allTranscriptOnly(libraryIds: string[]): boolean {
    // Every id must resolve to loaded data before this can answer "all". Dropping unresolved
    // ids and testing only the rest would weaken the quantifier: one transcript-only library
    // plus one whose `data` hasn't landed would report `true` and send the caller to transcript
    // mode, silently losing visual results for the second. Unverifiable means "no", so the
    // caller keeps its visual default.
    const settings = libraryIds
      .map((id) => this.libraryManager.libraryById(id)?.data)
      .filter((data): data is Library => !!data);
    return (
      settings.length > 0 &&
      settings.length === libraryIds.length &&
      settings.every(isLibraryTranscriptOnly)
    );
  }

  @computed
  get allScopeIsTranscriptOnly(): boolean {
    return this.allTranscriptOnly(
      this.libraryManager.selectedLibrariesToSearch.map((l) => l.libraryId),
    );
  }

  @computed
  get someScopeIsTranscriptOnly(): boolean {
    return this.transcriptOnlyLibrariesInScope.length > 0 && !this.allScopeIsTranscriptOnly;
  }

  /**
   * Single source of truth for the "Visual AI unavailable in X
   * {library/libraries}." copy shared between the search-mode tooltip
   * badge and the empty-state suggestion. Returns null when the
   * current scope has no transcript-only libraries.
   *
   *   - 1 transcript-only, all of scope → "Visual AI unavailable in this library."
   *   - N>1 transcript-only, all of scope → "Visual AI unavailable in these libraries."
   *   - Mixed scope → "Visual AI unavailable in some libraries."
   */
  @computed
  get transcriptOnlySentence(): string | null {
    if (this.allScopeIsTranscriptOnly) {
      const n = this.transcriptOnlyLibrariesInScope.length;
      return n === 1
        ? 'Visual AI unavailable in this library.'
        : 'Visual AI unavailable in these libraries.';
    }
    if (this.someScopeIsTranscriptOnly) {
      return 'Visual AI unavailable in some libraries.';
    }
    return null;
  }

  /**
   * True when the user ran a visual/unified search on a scope that
   * can't satisfy it and got zero results. SearchPage uses this to
   * swap MomentGrid for the TranscriptSearchSuggestion empty-state.
   *
   * Intentionally restricted to `allScopeIsTranscriptOnly` — on a mixed
   * scope we render the grid (with skeletons while loading) so the
   * visual-capable libraries' results can land there. Gating on
   * `someScopeIsTranscriptOnly` caused a flicker: between the request
   * being set and the visual-capable lib's `fetching` flag flipping
   * true, `anyLibrarySearching` was briefly false and the full-page
   * empty-state flashed in before the skeleton.
   *
   * Also stands down when the scope holds no media at all. Transcript-only-ness is
   * not why *that* search came back empty — an empty library returns nothing in
   * either mode — so "Visual AI unavailable in this library" would send the user off
   * to switch modes and search again just to be told the truth. The podcast clipper
   * provisions exactly this shape (an empty transcript-only library) and nothing
   * coerces `resultType` off its visual default, so it is the first thing such a user
   * would hit. `NoMomentsFound`'s `empty-scope` — "Nothing to search yet" plus Add
   * media — is the honest answer, and this getter yielding lets it through.
   */
  @computed
  get shouldSuggestTranscriptSearch(): boolean {
    const emptySearch = this.hasSearchTerms && !this.anyLibrarySearching;
    const noResults = !this.results?.results?.length;
    const modeNeedsVisuals = this.resultType === 'visual' || this.resultType === 'unified';
    const scope = this.libraryManager.selectedLibrariesToSearch;
    const nothingInScopeToSearch =
      scope.length > 0 && this.emptyLibrariesInScope.length === scope.length;
    if (nothingInScopeToSearch) return false;
    return emptySearch && noResults && modeNeedsVisuals && this.allScopeIsTranscriptOnly;
  }

  /**
   * True when the user ran a visual/unified search on a mixed scope
   * (some libraries transcript-only, some not) and got at least one
   * result. SearchPage uses this to render an informational footnote
   * below the results explaining that the transcript-only libraries
   * didn't contribute.
   */
  @computed
  get shouldShowTranscriptOnlyFootnote(): boolean {
    const settled = this.hasSearchTerms && !this.anyLibrarySearching;
    const hasAnyResults = !!this.results?.results?.length;
    const modeNeedsVisuals = this.resultType === 'visual' || this.resultType === 'unified';
    return settled && hasAnyResults && modeNeedsVisuals && this.someScopeIsTranscriptOnly;
  }

  /**
   * In-scope libraries whose search settled with an access error.
   *
   * 403 `user.library.forbidden` is the *only* thing the backend will say about a
   * library you can't search: its check is a membership test, so a library that
   * was deleted, one that was never yours, and one whose share was just revoked
   * all answer identically. Anything more specific would be a guess, which is why
   * the copy keyed off this hedges.
   *
   * Split from `failedLibrariesInScope` because the two want different offers —
   * "remove it from your search" for access, "try again" for a 500 or a dropped
   * connection. Retrying a 403 forever is the trap.
   */
  @computed
  get forbiddenLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter((l) =>
      isLibraryForbiddenError(l.search.error),
    );
  }

  /** In-scope libraries whose search settled with any error that isn't access. */
  @computed
  get failedLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter(
      (l) => !!l.search.error && !isLibraryForbiddenError(l.search.error),
    );
  }

  /** In-scope libraries holding no media at all. */
  @computed
  get emptyLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter((l) => l.isEmpty);
  }

  /** In-scope libraries holding media a search cannot reach yet, for any reason. */
  @computed
  get notReadyLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter((l) => l.mediaNotReadyCount > 0);
  }

  /**
   * In-scope libraries the backend is actively working through.
   *
   * The only signal that earns the words "still processing" — see
   * `mediaBackendWorkingCount`. `notReadyLibrariesInScope` is what decides *whether* to
   * show the state; this decides how confidently to describe it.
   */
  @computed
  get backendWorkingLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter(
      (l) => l.mediaBackendWorkingCount > 0,
    );
  }

  /** In-scope batches with work still outstanding, in either phase. */
  @computed
  get activeUploadBatchesInScope(): BatchNotification[] {
    const scope = this.libraryManager.selectedLibraryIdsToSearch;
    return notificationStore.notifications.filter(
      (n): n is BatchNotification =>
        (n.type === 'upload-file' || n.type === 'add-media-via-url') &&
        scope.has(n.libraryId) &&
        notificationStore.isActive(n),
    );
  }

  /**
   * Whether media is on its way into a library in scope, at any stage between
   * "bytes are moving" and "the backend has finished with it".
   *
   * Covers what `stats` alone cannot: a library whose stats haven't landed yet reads as
   * empty mid-upload, and `mediaNotReadyCount` can only ever describe rows the backend
   * has already created.
   *
   * **Spans the whole batch, not just the transfer, and that is the fix for a real
   * bug.** Keyed on `uploadProgress < 1` this went false the instant the transfer
   * finished, and nothing picked up the baton: `refreshLibrary` fires at upload success,
   * when the backend row is still `empty`, which the then-narrower stats count excluded
   * — so for the whole `empty` → `queued` window the page fell through to "No moments
   * match". Observed in the running app; it was a guess about backend transition timing
   * that turned out to be wrong.
   *
   * Read from the notification rather than `stats`, which is what makes it immune to
   * the orphan problem: `isActive` is the store's own source of truth for "has work
   * the user is waiting on", and a cancelled batch reports both phases complete.
   *
   * Not `activeMediaNotifications` — that requires `uploadProgress >= 1`, so it
   * describes the processing phase only and excludes the transfer entirely.
   */
  @computed
  get hasActiveUploadBatchInScope(): boolean {
    return this.activeUploadBatchesInScope.length > 0;
  }

  /**
   * Whether bytes are still moving, as opposed to the backend working through media
   * it already has. Only picks the verb in the copy — "uploading" vs "processing".
   */
  @computed
  get isTransferringInScope(): boolean {
    return this.activeUploadBatchesInScope.some(
      (n) => n.type === 'upload-file' && n.uploadProgress < 1,
    );
  }

  /**
   * The library to offer as somewhere to watch the upload, when exactly one is
   * involved. Null when several are, since there is no single place to open then.
   */
  @computed
  get soleActiveUploadLibraryIdInScope(): string | null {
    const ids = new Set([
      ...this.activeUploadBatchesInScope.map((n) => n.libraryId),
      ...this.notReadyLibrariesInScope.map((l) => l.libraryId),
    ]);
    return ids.size === 1 ? [...ids][0] : null;
  }

  /** In-scope libraries whose every media row failed. */
  @computed
  get failedMediaLibrariesInScope() {
    return this.libraryManager.selectedLibrariesToSearch.filter((l) => l.hasOnlyFailedMedia);
  }

  /**
   * Every selected library we could not search, whether it went missing from the
   * library list or answered 403 — the set the empty state's "Remove from search"
   * clears.
   *
   * Ids rather than stores because half of them have no store; that asymmetry is
   * also why the copy can only name a library sometimes.
   */
  @computed
  get unsearchableLibraryIdsInScope(): string[] {
    return [
      ...this.libraryManager.unreachableLibraryIdsToSearch,
      ...this.forbiddenLibrariesInScope.map((l) => l.libraryId),
    ];
  }

  /**
   * Why a settled search has nothing to show, or `null` while there is something
   * to render (results, or a search still in flight).
   *
   * `SearchPage` swaps `MomentGrid` for `NoMomentsFound` on this, the same way it
   * already swaps in `TranscriptSearchSuggestion` on
   * `shouldSuggestTranscriptSearch`. That state stays ahead of this one: it is the
   * more specific answer for a scope that cannot serve visual results at all.
   *
   * The precedence between the reasons lives in `resolveZeroResultReason` so it can
   * be tested without a store; this getter only gathers the counts.
   */
  @computed
  get zeroResultReason(): ZeroResultReason | null {
    if (!this.hasSearchTerms) return null;

    return resolveZeroResultReason({
      selectedCount: this.libraryManager.selectedLibraryIdsToSearch.size,
      unreachableCount: this.libraryManager.unreachableLibraryIdsToSearch.length,
      forbiddenCount: this.forbiddenLibrariesInScope.length,
      failedCount: this.failedLibrariesInScope.length + this.unresolvedLibraryIdsInScope.length,
      emptyCount: this.emptyLibrariesInScope.length,
      processingCount: this.notReadyLibrariesInScope.length,
      uploadInFlight: this.hasActiveUploadBatchInScope,
      failedMediaOnlyCount: this.failedMediaLibrariesInScope.length,
      searchableCount: this.libraryManager.selectedLibrariesToSearch.length,
      scopeKnown: this.libraryManager.areLibraryStatsLoaded,
      inFlight: this.anyLibrarySearching,
      settled: this.allLibrariesSettledSearch,
      hasResults: !!this.results?.results?.length,
    });
  }

  @computed
  get results() {
    if (!this.allLibrariesFinishedSearch) return null;
    if (!this.hasSearchTerms) return null;

    const medias: Record<string, Media> = {};
    let results: (SearchResultItem & { libraryId: string })[] = [];

    for (const library of this.libraryManager.selectedLibrariesToSearch) {
      const libraryResults = library.search.data;
      if (!libraryResults) {
        // Only a real error is worth a warning. `data` is also null in the
        // ordinary window between a request being set and the debounced fetch
        // resolving — `fetching` is still false there, so this branch used to warn
        // on every healthy search and made a genuine 403 indistinguishable from
        // routine startup noise. `zeroResultReason` is what actually reports the
        // failure to the user now.
        if (library.search.error) {
          console.warn(`Library ${library.libraryId} could not be searched`, library.search.error);
        }
        continue;
      }

      Object.assign(medias, libraryResults.media);

      const libraryId = library.libraryId;
      results = results.concat(libraryResults.results.map((r) => ({ ...r, libraryId })));
    }

    results.sort((a, b) => b.score - a.score);

    return { medias, results };
  }

  constructor(private readonly libraryManager: LibraryManager) {
    super();
    makeObservable(this);

    this.momentSelection = new MomentSelection(this);

    // Scope changes re-run the searches; they deliberately do NOT re-derive `resultType`. See the
    // note above `fallbackResultType` before adding that — it reads as a bug and isn't one.
    this.reaction(() => this.libraryManager.selectedLibrariesToSearch, this.updateLibrarySearches);

    this.reaction(() => this.resultType, this.updateLibrarySearches);
    this.reaction(() => this.mediaId, this.updateLibrarySearches);

    this.reaction(
      // crude implementaiton to detect changes in the whole object
      // deepObserve doesn't work because we replace the object with a new one
      () => JSON.stringify(this.request),
      this.updateLibrarySearches,
    );

    // Passing arrow functions to avoid having the wrong "this" context
    this.reaction(
      () => this.request,
      () => this.momentSelection.clearSelection(),
    );
  }

  @action
  clearSearch() {
    this.request = null;
  }

  @action
  setSearchRequest(request: SearchRequest | null) {
    this.request = request;
  }

  @action
  setSearchText(text: string | null) {
    if (!this.request) this.request = {} as SearchRequest;

    if (text === null || text.trim() === '') {
      this.request.text = undefined;
      if (areAllValuesEmpty(this.request)) {
        this.request = null;
      }
    } else {
      this.request.text = text;
    }
  }

  @action
  setSearchImage(image: File | null) {
    if (!this.request) this.request = {} as SearchRequest;

    const request = this.request;
    if (image === null) {
      request.image = undefined;
      if (areAllValuesEmpty(this.request)) {
        this.request = null;
      }
    } else {
      fileToBase64(image)
        .then(
          action((b64Image: string) => {
            request.image = b64Image;
          }),
        )
        .catch((e: Error) => {
          console.error(e);
        });
    }
  }

  @action
  setSearchClip(resultId: string | null, preview: string | null = null, resetText: boolean = true) {
    if (!this.request) this.request = {} as SearchRequest;

    if (resultId === null) {
      this.request.clipId = undefined;
      if (areAllValuesEmpty(this.request)) {
        this.request = null;
      }
      this.searchClipPreview = null;
    } else {
      this.request.clipId = resultId;
      if (resetText) this.request.text = undefined;
      trackingStore.searchClipUsed({ clipId: resultId, resetText });
      const clip = this.results?.results.find((r) => r.id === resultId);
      if (clip && preview) {
        this.searchClipPreview = { clip, preview };
      }
    }
  }

  @action
  updateLibrarySearches = () => {
    if (this._searchTrackTimeout) clearTimeout(this._searchTrackTimeout);
    if (this.request && this.hasSearchTerms) {
      const snapshot = {
        query: this.request.text,
        hasImage: !!this.request.image,
        hasClip: !!this.request.clipId,
        resultType: this.resultType,
        libraryCount: this.libraryManager.selectedLibrariesToSearch.length,
      };
      this._searchTrackTimeout = setTimeout(() => {
        trackingStore.searchPerformed(snapshot);
      }, 500);
    }
    // Visual and Unified searches can't produce results against
    // audio-centric libraries. Firing the request anyway causes a
    // loading flicker followed by an empty response. Clear their
    // request instead so they resolve instantly with no results and
    // the empty-state suggestion (or visual-capable libs' results)
    // render immediately.
    const isVisualOrUnified = this.resultType === 'visual' || this.resultType === 'unified';
    for (const library of this.libraryManager.selectedLibrariesToSearch) {
      const skipFetch = isVisualOrUnified && isLibraryTranscriptOnly(library.data);
      library.search.setSearchRequest(
        skipFetch ? null : this.request,
        this.resultType,
        this.mediaId,
      );
    }
  };
}
